import { createSlice } from "@reduxjs/toolkit";
import {
  // Metal Rate thunks
  setRateThunk,
  setManualRateWithChargesThunk,
  getRateDashboardThunk,
  // approveRateThunk,
  getRateAnalyticsThunk,

  // Daily Rate thunks
  setDailyRateThunk,
  fetchFromAPIThunk,
  confirmAPIRatesThunk,
  scheduleRateThunk,
  copyPreviousDayThunk,
  getTodayRateThunk,
  morningCheckThunk,
  bulkUpdateRatesThunk,

  // Stone Rate thunks
  getStoneRateThunk,
  setStoneRateThunk,
  bulkImportStoneRatesThunk,
  getDiamondMatrixThunk,
  calculateStoneValueThunk,

  // Market Rate thunks
  fetchMarketRatesThunk,
  compareWithMarketThunk,
  syncFromMarketThunk,
  getMarketHistoryThunk,

  // Branch Config thunks
  configureRateModeThunk,
  setMainBranchThunk,
  syncRatesFromMainThunk,
  copyRatesThunk,

  // Charges Config thunks
  setGSTRateThunk,
  setCustomChargeThunk,

  // Calculation thunks
  calculateProductValueThunk,
  getAllRatesWithChargesThunk,

  // Actions
  clearRateError,
  clearDashboard,
  clearMarketData,
  clearStoneRates,
  clearCalculation,
  resetRateState,
  setSelectedDate,
  setSelectedBranch,
  configureBranchInheritanceThunk,
  getRateStatusThunk,
  getRateHistoryThunk,
  deleteRateThunk,
  updateRateThunk,
  exportRatesThunk,
  clearBranchRates,
  setAPIRates,
  getChargesConfigThunk,
} from "../thunks/rateThunks";

const initialState = {
  // Dashboard Data
  dashboard: {
    metalRates: {},
    branchRates: {},
    configuration: null,
    pendingApprovals: 0,
    location: null,
  },

  // Current Rates
  currentRates: {
    GOLD: null,
    SILVER: null,
    PLATINUM: null,
    DIAMOND: null,
  },

  // Daily Rates
  dailyRates: {
    byBranch: {},
    today: null,
    scheduled: [],
    history: [],
    apiRates: null,
    morningCheckStatus: null,
  },

  // Stone Rates
  stoneRates: {
    current: null,
    diamondMatrix: null,
    calculatedValue: null,
    importResults: null,
  },

  // Market Data
  marketData: {
    currentRates: null,
    comparison: null,
    history: [],
    syncResults: null,
  },

  // Branch Configuration
  branchConfig: {
    mainBranch: null,
    branchModes: {},
    inheritanceSettings: {},
    syncStatus: null,
  },

  // Charges Configuration
  charges: {
    gst: null,
    making: null,
    wastage: null,
    hallmark: null,
    custom: {},
  },

  // Analytics
  analytics: {
    data: null,
    dateRange: null,
  },

  // Calculation Results
  calculation: {
    productValue: null,
    allRatesWithCharges: null,
  },

  // UI State
  ui: {
    activeTab: "dashboard",
    selectedBranch: "", // Add this
    selectedDate: new Date().toISOString().split("T")[0],
    filters: {
      metalType: "ALL",
      dateRange: "TODAY",
    },
  },

  // Loading States
  loading: {
    dashboard: false,
    rates: false,
    dailyRates: false,
    stoneRates: false,
    market: false,
    analytics: false,
    branchConfig: false,
    calculation: false,
  },

  // Saving States
  saving: {
    rates: false,
    dailyRates: false,
    stoneRates: false,
    charges: false,
    branchConfig: false,
  },

  // Errors
  errors: {
    dashboard: null,
    rates: null,
    dailyRates: null,
    stoneRates: null,
    market: null,
    charges: null,
    branchConfig: null,

    general: null,
  },
};

const rateSlice = createSlice({
  name: "rate",
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.ui.activeTab = action.payload;
    },
    setSelectedBranch: (state, action) => {
      state.ui.selectedBranch = action.payload;
    },
    setSelectedDate: (state, action) => {
      state.ui.selectedDate = action.payload;
    },
    setFilters: (state, action) => {
      state.ui.filters = { ...state.ui.filters, ...action.payload };
    },
    setSelectedMetalType: (state, action) => {
      state.ui.selectedMetalType = action.payload;
    },
    updateCurrentRate: (state, action) => {
      const { type, rate } = action.payload;
      state.currentRates[type] = rate;
    },
    clearErrors: (state) => {
      state.error = initialState.error;
    },
    clearAllErrors: (state) => {
      state.errors = initialState.errors;
    },
    resetRateState: () => initialState,
    clearBranchRates: (state, action) => {
      const branchCode = action.payload;
      if (state.dailyRates.byBranch[branchCode]) {
        delete state.dailyRates.byBranch[branchCode];
      }
    },
    setAPIRatesForBranch: (state, action) => {
      const { branchCode, apiRates } = action.payload;
      if (!state.dailyRates.byBranch[branchCode]) {
        state.dailyRates.byBranch[branchCode] = {};
      }
      state.dailyRates.byBranch[branchCode].apiRates = apiRates;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GENERAL ACTIONS =====
      .addCase(clearRateError, (state) => {
        state.errors = initialState.errors;
      })
      .addCase(clearDashboard, (state) => {
        state.dashboard = initialState.dashboard;
      })
      .addCase(clearMarketData, (state) => {
        state.marketData = initialState.marketData;
      })
      .addCase(clearStoneRates, (state) => {
        state.stoneRates = initialState.stoneRates;
      })
      .addCase(clearCalculation, (state) => {
        state.calculation = initialState.calculation;
      })
      .addCase(resetRateState, () => initialState)
      .addCase(setSelectedDate, (state, action) => {
        state.ui.selectedDate = action.payload;
      })
      .addCase(setSelectedBranch, (state, action) => {
        state.ui.selectedBranch = action.payload;
      })

      // ===== SET RATE =====
      .addCase(setRateThunk.pending, (state) => {
        state.saving.rates = true;
        state.errors.rates = null;
      })
      .addCase(setRateThunk.fulfilled, (state, action) => {
        state.saving.rates = false;
        const rateData = action.payload.data || action.payload;
        if (rateData.type && state.currentRates[rateData.type] !== undefined) {
          state.currentRates[rateData.type] = rateData;
        }
      })
      .addCase(setRateThunk.rejected, (state, action) => {
        state.saving.rates = false;
        state.errors.rates = action.payload;
      })

      // ===== SET MANUAL RATE WITH CHARGES =====
      .addCase(setManualRateWithChargesThunk.pending, (state) => {
        state.saving.rates = true;
        state.errors.rates = null;
      })
      .addCase(setManualRateWithChargesThunk.fulfilled, (state, action) => {
        state.saving.rates = false;
        const rateData = action.payload.data || action.payload;
        if (rateData.type && state.currentRates[rateData.type] !== undefined) {
          state.currentRates[rateData.type] = rateData;
        }
        // Update charges if present
        if (rateData.charges) {
          Object.assign(state.charges, rateData.charges);
        }
      })
      .addCase(setManualRateWithChargesThunk.rejected, (state, action) => {
        state.saving.rates = false;
        state.errors.rates = action.payload;
      })

      // ===== GET RATE DASHBOARD =====
      .addCase(getRateDashboardThunk.pending, (state) => {
        state.loading.dashboard = true;
        state.errors.dashboard = null;
      })
      .addCase(getRateDashboardThunk.fulfilled, (state, action) => {
        state.loading.dashboard = false;
        const data = action.payload.data || action.payload;
        state.dashboard = data;
        // Update current rates from dashboard
        if (data.metalRates) {
          Object.keys(data.metalRates).forEach((type) => {
            if (state.currentRates[type] !== undefined) {
              state.currentRates[type] = data.metalRates[type];
            }
          });
        }
      })
      .addCase(getRateDashboardThunk.rejected, (state, action) => {
        state.loading.dashboard = false;
        state.errors.dashboard = action.payload;
      })

      // ===== APPROVE RATE =====
      // .addCase(approveRateThunk.pending, (state) => {
      //   state.saving.rates = true;
      //   state.errors.rates = null;
      // })
      // .addCase(approveRateThunk.fulfilled, (state, action) => {
      //   state.saving.rates = false;
      //   // Reduce pending approvals count
      //   if (state.dashboard.pendingApprovals > 0) {
      //     state.dashboard.pendingApprovals -= 1;
      //   }
      // })
      // .addCase(approveRateThunk.rejected, (state, action) => {
      //   state.saving.rates = false;
      //   state.errors.rates = action.payload;
      // })

      // ===== GET RATE ANALYTICS =====
      .addCase(getRateAnalyticsThunk.pending, (state) => {
        state.loading.analytics = true;
        state.errors.general = null;
      })
      .addCase(getRateAnalyticsThunk.fulfilled, (state, action) => {
        state.loading.analytics = false;
        state.analytics.data = action.payload.data || action.payload;
      })
      .addCase(getRateAnalyticsThunk.rejected, (state, action) => {
        state.loading.analytics = false;
        state.errors.general = action.payload;
      })

      // ===== DAILY RATES =====

      // Set Daily Rate
      .addCase(setDailyRateThunk.pending, (state) => {
        state.saving.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(setDailyRateThunk.rejected, (state, action) => {
        state.saving.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Fetch From API
      .addCase(fetchFromAPIThunk.pending, (state) => {
        state.loading.dailyRates = true;
        state.errors.dailyRates = null;
      })

      .addCase(fetchFromAPIThunk.rejected, (state, action) => {
        state.loading.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Confirm API Rates
      .addCase(confirmAPIRatesThunk.pending, (state) => {
        state.saving.dailyRates = true;
        state.errors.dailyRates = null;
      })

      .addCase(confirmAPIRatesThunk.rejected, (state, action) => {
        state.saving.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Get Today Rate
      .addCase(getTodayRateThunk.pending, (state) => {
        state.loading.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(getTodayRateThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        const response = action.payload;
        const data = response.data;
        const isFallback = response.isFallback || false;
        const branchCode = action.meta.arg?.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }

        state.dailyRates.byBranch[branchCode].today = data;
        state.dailyRates.byBranch[branchCode].isFallback = isFallback;
      })
      .addCase(getTodayRateThunk.rejected, (state, action) => {
        state.loading.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Morning Check
      .addCase(morningCheckThunk.pending, (state) => {
        state.loading.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(morningCheckThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        state.dailyRates.morningCheckStatus =
          action.payload.data || action.payload;
      })
      .addCase(morningCheckThunk.rejected, (state, action) => {
        state.loading.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })
      .addCase(setDailyRateThunk.fulfilled, (state, action) => {
        state.saving.dailyRates = false;
        const dailyRate = action.payload.data || action.payload;
        const branchCode = action.meta.arg.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }
        state.dailyRates.byBranch[branchCode].today = dailyRate;
      })

      // Fetch From API - UPDATE
      .addCase(fetchFromAPIThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        const apiData = action.payload.data || action.payload;
        const branchCode = action.meta.arg.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }
        state.dailyRates.byBranch[branchCode].apiRates = apiData;
      })

      // Confirm API Rates - UPDATE
      .addCase(confirmAPIRatesThunk.fulfilled, (state, action) => {
        state.saving.dailyRates = false;
        const confirmedRate = action.payload.data || action.payload;
        const branchCode = action.meta.arg.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }
        state.dailyRates.byBranch[branchCode].today = confirmedRate;
        state.dailyRates.byBranch[branchCode].apiRates = null;
      })

      // Copy Previous Day - UPDATE
      .addCase(copyPreviousDayThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        const copiedRate = action.payload.data || action.payload;
        const branchCode = action.meta.arg.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }
        state.dailyRates.byBranch[branchCode].today = copiedRate;
      })

      // ADD NEW CASES for the new thunks:

      // Get Rate Status
      .addCase(getRateStatusThunk.pending, (state) => {
        state.loading.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(getRateStatusThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        const status = action.payload.data || action.payload;
        const branchCode = action.meta.arg.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }
        state.dailyRates.byBranch[branchCode].history = status;
      })
      .addCase(getRateStatusThunk.rejected, (state, action) => {
        state.loading.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Get Rate History
      .addCase(getRateHistoryThunk.pending, (state) => {
        state.loading.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(getRateHistoryThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        const history = action.payload.data || action.payload;
        const branchCode = action.meta.arg?.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }
        state.dailyRates.byBranch[branchCode].fullHistory = history;
      })
      .addCase(getRateHistoryThunk.rejected, (state, action) => {
        state.loading.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Delete Rate
      .addCase(deleteRateThunk.pending, (state) => {
        state.loading.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(deleteRateThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        const { rateId, branchCode } = action.payload;

        // Remove from history if present
        if (state.dailyRates.byBranch[branchCode]?.fullHistory) {
          state.dailyRates.byBranch[branchCode].fullHistory =
            state.dailyRates.byBranch[branchCode].fullHistory.filter(
              (rate) => rate._id !== rateId
            );
        }

        // Clear today if it matches
        if (state.dailyRates.byBranch[branchCode]?.today?._id === rateId) {
          state.dailyRates.byBranch[branchCode].today = null;
        }
      })
      .addCase(deleteRateThunk.rejected, (state, action) => {
        state.loading.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Update Rate
      .addCase(updateRateThunk.pending, (state) => {
        state.saving.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(updateRateThunk.fulfilled, (state, action) => {
        state.saving.dailyRates = false;
        const updatedRate = action.payload.data || action.payload;
        const branchCode = action.payload.branchCode || "default";

        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }

        // Update in today if it matches
        if (
          state.dailyRates.byBranch[branchCode].today?._id === updatedRate._id
        ) {
          state.dailyRates.byBranch[branchCode].today = updatedRate;
        }

        // Update in history if present
        if (state.dailyRates.byBranch[branchCode].fullHistory) {
          const index = state.dailyRates.byBranch[
            branchCode
          ].fullHistory.findIndex((rate) => rate._id === updatedRate._id);
          if (index !== -1) {
            state.dailyRates.byBranch[branchCode].fullHistory[index] =
              updatedRate;
          }
        }
      })
      .addCase(updateRateThunk.rejected, (state, action) => {
        state.saving.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Export Rates
      .addCase(exportRatesThunk.pending, (state) => {
        state.loading.dailyRates = true;
        state.errors.dailyRates = null;
      })
      .addCase(exportRatesThunk.fulfilled, (state, action) => {
        state.loading.dailyRates = false;
        // Export doesn't change state, just triggers download
      })
      .addCase(exportRatesThunk.rejected, (state, action) => {
        state.loading.dailyRates = false;
        state.errors.dailyRates = action.payload;
      })

      // Clear Branch Rates action
      .addCase(clearBranchRates, (state, action) => {
        const branchCode = action.payload;
        if (state.dailyRates.byBranch[branchCode]) {
          delete state.dailyRates.byBranch[branchCode];
        }
      })

      // Set API Rates action
      .addCase(setAPIRates, (state, action) => {
        const { branchCode, apiRates } = action.payload;
        if (!state.dailyRates.byBranch[branchCode]) {
          state.dailyRates.byBranch[branchCode] = {};
        }
        state.dailyRates.byBranch[branchCode].apiRates = apiRates;
      })

      // ===== STONE RATES =====

      // Get Stone Rate
      .addCase(getStoneRateThunk.pending, (state) => {
        state.loading.stoneRates = true;
        state.errors.stoneRates = null;
      })
      .addCase(getStoneRateThunk.fulfilled, (state, action) => {
        state.loading.stoneRates = false;
        state.stoneRates.current = action.payload.data || action.payload;
      })
      .addCase(getStoneRateThunk.rejected, (state, action) => {
        state.loading.stoneRates = false;
        state.errors.stoneRates = action.payload;
      })

      // Set Stone Rate
      .addCase(setStoneRateThunk.pending, (state) => {
        state.saving.stoneRates = true;
        state.errors.stoneRates = null;
      })
      .addCase(setStoneRateThunk.fulfilled, (state, action) => {
        state.saving.stoneRates = false;
        state.stoneRates.current = action.payload.data || action.payload;
      })
      .addCase(setStoneRateThunk.rejected, (state, action) => {
        state.saving.stoneRates = false;
        state.errors.stoneRates = action.payload;
      })

      // Get Diamond Matrix
      .addCase(getDiamondMatrixThunk.pending, (state) => {
        state.loading.stoneRates = true;
        state.errors.stoneRates = null;
      })
      .addCase(getDiamondMatrixThunk.fulfilled, (state, action) => {
        state.loading.stoneRates = false;
        state.stoneRates.diamondMatrix = action.payload.data || action.payload;
      })
      .addCase(getDiamondMatrixThunk.rejected, (state, action) => {
        state.loading.stoneRates = false;
        state.errors.stoneRates = action.payload;
      })

      // ===== MARKET RATES =====

      // Fetch Market Rates
      .addCase(fetchMarketRatesThunk.pending, (state) => {
        state.loading.market = true;
        state.errors.market = null;
      })
      .addCase(fetchMarketRatesThunk.fulfilled, (state, action) => {
        state.loading.market = false;
        state.marketData.currentRates = action.payload.data || action.payload;
      })
      .addCase(fetchMarketRatesThunk.rejected, (state, action) => {
        state.loading.market = false;
        state.errors.market = action.payload;
      })

      // Compare With Market
      .addCase(compareWithMarketThunk.pending, (state) => {
        state.loading.market = true;
        state.errors.market = null;
      })
      .addCase(compareWithMarketThunk.fulfilled, (state, action) => {
        state.loading.market = false;
        state.marketData.comparison = action.payload.data || action.payload;
      })
      .addCase(compareWithMarketThunk.rejected, (state, action) => {
        state.loading.market = false;
        state.errors.market = action.payload;
      })

      // Sync From Market
      .addCase(syncFromMarketThunk.pending, (state) => {
        state.saving.rates = true;
        state.errors.market = null;
      })
      .addCase(syncFromMarketThunk.fulfilled, (state, action) => {
        state.saving.rates = false;
        state.marketData.syncResults = action.payload.data || action.payload;
      })
      .addCase(syncFromMarketThunk.rejected, (state, action) => {
        state.saving.rates = false;
        state.errors.market = action.payload;
      })

      // ===== CHARGES =====

      // Set GST Rate
      .addCase(setGSTRateThunk.pending, (state) => {
        state.saving.charges = true;
        state.errors.charges = null;
      })
      .addCase(setGSTRateThunk.fulfilled, (state, action) => {
        state.saving.charges = false;
        const gstData = action.payload.data || action.payload;
        state.charges.gst = gstData;
      })
      .addCase(setGSTRateThunk.rejected, (state, action) => {
        state.saving.charges = false;
        state.errors.charges = action.payload;
      })

      // Set Custom Charge
      .addCase(setCustomChargeThunk.pending, (state) => {
        state.saving.charges = true;
        state.errors.charges = null;
      })
      .addCase(setCustomChargeThunk.fulfilled, (state, action) => {
        state.saving.charges = false;
        const chargeData = action.payload.data || action.payload;
        if (chargeData.chargeType) {
          // Update the specific charge type in state.charges directly
          state.charges[chargeData.chargeType] = chargeData.configuration;
        }
      })
      .addCase(setCustomChargeThunk.rejected, (state, action) => {
        state.saving.charges = false;
        state.errors.charges = action.payload;
      })

      // ===== CALCULATIONS =====

      // Calculate Product Value
      .addCase(calculateProductValueThunk.pending, (state) => {
        state.loading.calculation = true;
        state.errors.general = null;
      })
      .addCase(calculateProductValueThunk.fulfilled, (state, action) => {
        state.loading.calculation = false;
        state.calculation.productValue = action.payload.data || action.payload;
      })
      .addCase(calculateProductValueThunk.rejected, (state, action) => {
        state.loading.calculation = false;
        state.errors.general = action.payload;
      })

      // Get All Rates With Charges
      .addCase(getAllRatesWithChargesThunk.pending, (state) => {
        state.loading.rates = true;
        state.errors.rates = null;
      })
      .addCase(getAllRatesWithChargesThunk.fulfilled, (state, action) => {
        state.loading.rates = false;
        state.calculation.allRatesWithCharges =
          action.payload.data || action.payload;
      })
      .addCase(getAllRatesWithChargesThunk.rejected, (state, action) => {
        state.loading.rates = false;
        state.errors.rates = action.payload;
      }) // Configure Branch Inheritance
      .addCase(configureBranchInheritanceThunk.pending, (state) => {
        state.saving.branchConfig = true;
        state.errors.general = null;
      })
      .addCase(configureBranchInheritanceThunk.fulfilled, (state, action) => {
        state.saving.branchConfig = false;
        const { branchCode, inheritanceData } = action.meta.arg;
        state.branchConfig.inheritanceSettings[branchCode] = inheritanceData;
      })
      .addCase(configureBranchInheritanceThunk.rejected, (state, action) => {
        state.saving.branchConfig = false;
        state.errors.general = action.payload;
      })

      // Configure Rate Mode
      .addCase(configureRateModeThunk.pending, (state) => {
        state.saving.branchConfig = true;
        state.errors.general = null;
      })
      .addCase(configureRateModeThunk.fulfilled, (state, action) => {
        state.saving.branchConfig = false;
        const { branchCode, modeData } = action.meta.arg;
        state.branchConfig.branchModes[branchCode] = modeData.mode;
      })
      .addCase(configureRateModeThunk.rejected, (state, action) => {
        state.saving.branchConfig = false;
        state.errors.general = action.payload;
      })

      // Add cases for hybrid policy configuration

      // Set Main Branch
      .addCase(setMainBranchThunk.pending, (state) => {
        state.saving.branchConfig = true;
        state.errors.general = null;
      })
      .addCase(setMainBranchThunk.fulfilled, (state, action) => {
        state.saving.branchConfig = false;
        state.branchConfig.mainBranch = action.meta.arg;
      })
      .addCase(setMainBranchThunk.rejected, (state, action) => {
        state.saving.branchConfig = false;
        state.errors.general = action.payload;
      })

      // Copy Rates
      .addCase(copyRatesThunk.pending, (state) => {
        state.saving.rates = true;
        state.errors.rates = null;
      })
      .addCase(copyRatesThunk.fulfilled, (state, action) => {
        state.saving.rates = false;
        // You might want to refresh rates after copying
      })
      .addCase(copyRatesThunk.rejected, (state, action) => {
        state.saving.rates = false;
        state.errors.rates = action.payload;
      })

      // Sync Rates From Main
      .addCase(syncRatesFromMainThunk.pending, (state) => {
        state.saving.rates = true;
        state.errors.rates = null;
      })
      .addCase(syncRatesFromMainThunk.fulfilled, (state, action) => {
        state.saving.rates = false;
        state.branchConfig.syncStatus = "completed";
      })
      .addCase(syncRatesFromMainThunk.rejected, (state, action) => {
        state.saving.rates = false;
        state.errors.rates = action.payload;
        state.branchConfig.syncStatus = "failed";
      })
      .addCase(getChargesConfigThunk.pending, (state) => {
        state.loading.charges = true;
        state.errors.charges = null;
      })
      .addCase(getChargesConfigThunk.fulfilled, (state, action) => {
        state.loading.charges = false;
        const chargesData = action.payload.data || action.payload;
        state.charges = chargesData;
      })
      .addCase(getChargesConfigThunk.rejected, (state, action) => {
        state.loading.charges = false;
        state.errors.charges = action.payload;
      });
  },
});

export const {
  setActiveTab,
  setSelectedMetalType,
  setFilters,
  clearErrors,
  updateCurrentRate,
  clearAllErrors,
  clearBranchRatesAction,
  setAPIRatesForBranch,
} = rateSlice.actions;

export default rateSlice.reducer;
